'use client';

/**
 * ProductCarousel - Main product image carousel with thumbnail strip
 *
 * COMPONENT STATUS: Active development - mobile touch handling
 *
 * RECENT BUG FIXES (May 2026):
 * 1. iOS race condition (e8b193a0): Tap on thumb triggered both onClick AND synthetic
 *    hover, causing double scrollTo. Fixed by using ref instead of state for hoverDisabled.
 *
 * 2. Mobile scroll direction (31de980d): With loop enabled, swipe direction felt inverted
 *    at edges. Fixed by disabling loop on mobile via breakpoint: { loop: false }.
 *
 * 3. Touch swipe locking (b95851f1): useHoverIntent was capturing touch events and
 *    preventing swipe. Fixed in CarouselThumb.js by disabling useHoverIntent on touch.
 *
 * 4. Thumb not centering on swipe (current): When swiping main image on mobile, thumb
 *    strip didn't scroll to show active thumb. Fixed by adding emblaThumbsAPI.scrollTo()
 *    in onSelect for touch devices only.
 *
 * BEHAVIOR BY DEVICE:
 * - Desktop: Hover thumb = preview main image (thumb strip stays put)
 *            Click thumb = center thumb + open modal
 * - Mobile:  Tap thumb = center thumb + change main image
 *            Swipe main = change main image + center thumb
 */
import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
//import { useWindowSize } from '@/app/_lib/hooks'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faChevronLeft } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faPlay } from '@awesome.me/kit-94e593bf23/icons/sharp/solid';
import { faPause } from '@awesome.me/kit-94e593bf23/icons/sharp/solid';
import { faExpand } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import '@/app/_styles/product_carousel.css';
import '@/app/_styles/carousel_thumbs.css';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Fade from 'embla-carousel-fade';
import { Thumb } from "./CarouselThumb";
import VideoSlide from "./VideoSlide";
import VideoModal from "./VideoModal";
import SiteCover from '@/app/_components/SiteCover';
import ProductCarouselModal from "./ProductCarouselModal";
import { useAutoplayProgress } from './EmbalAutoplayProgress';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
const ProductCarousel = t0 => {
  const $ = _c(169);
  const {
    sub_product,
    productImages,
    video,
    variant
  } = t0;
  let t1;
  if ($[0] !== productImages || $[1] !== video) {
    t1 = video ? [...productImages, {
      type: "video",
      ...video
    }] : productImages;
    $[0] = productImages;
    $[1] = video;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const slides = t1;
  const [open] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [carouselModalOpen, setCarouselModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [arrowsHidden, setArrowsHidden] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const hoverDisabledRef = useRef(false);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 0,
      y: 0
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [cursorPos, setCursorPos] = useState(t2);
  const [cursorVisible, setCursorVisible] = useState(false);
  const imageContainerRef = useRef(null);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setCarouselModalOpen(true);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const openModal = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setCarouselModalOpen(false);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const closeModal = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setVideoModalOpen(true);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const openVideoModal = t5;
  let t6;
  if ($[7] !== variant) {
    t6 = () => {
      if (!variant) {
        return;
      }
      return false;
    };
    $[7] = variant;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleSlideChange = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      "(max-width: 768px)": {
        duration: 30,
        loop: false
      }
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== handleSlideChange) {
    t8 = {
      loop: true,
      skipSnaps: false,
      dragFree: false,
      align: "center",
      dragThreshold: 10,
      duration: 150,
      breakpoints: t7,
      watchSlides: handleSlideChange
    };
    $[10] = handleSlideChange;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const [emplaOptions] = useState(t8);
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      delay: 5000,
      playOnInit: false
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const [autoplayOptions] = useState(t9);
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      breakpoints: {
        "(max-width: 768px)": {
          active: false
        }
      }
    };
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const [fadeOptions] = useState(t10);
  let t11;
  if ($[14] !== fadeOptions) {
    t11 = Fade(fadeOptions);
    $[14] = fadeOptions;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== autoplayOptions) {
    t12 = Autoplay(autoplayOptions);
    $[16] = autoplayOptions;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11 || $[19] !== t12) {
    t13 = [t11, t12];
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const [mainViewportRef, emblaAPI] = useEmblaCarousel(emplaOptions, t13);
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      containScroll: "keepSnaps",
      dragFree: true
    };
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const [emblaThumbOptions] = useState(t14);
  const [emblaThumbsRef, emblaThumbsAPI] = useEmblaCarousel(emblaThumbOptions);
  let t15;
  if ($[22] !== emblaAPI) {
    t15 = index => {
      if (!emblaAPI || hoverDisabledRef.current) {
        return;
      }
      emblaAPI.scrollTo(index);
    };
    $[22] = emblaAPI;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const onThumbHover = t15;
  let t16;
  if ($[24] !== emblaAPI || $[25] !== emblaThumbsAPI) {
    t16 = index_0 => {
      if (!emblaAPI || !emblaThumbsAPI) {
        return;
      }
      hoverDisabledRef.current = true;
      emblaAPI.scrollTo(index_0);
      emblaThumbsAPI.scrollTo(index_0);
      const onSettle = () => {
        hoverDisabledRef.current = false;
        emblaThumbsAPI.off("settle", onSettle);
        clearTimeout(fallback);
      };
      emblaThumbsAPI.on("settle", onSettle);
      const fallback = setTimeout(() => {
        hoverDisabledRef.current = false;
        emblaThumbsAPI.off("settle", onSettle);
      }, 100);
    };
    $[24] = emblaAPI;
    $[25] = emblaThumbsAPI;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const onThumbClick = t16;
  let t17;
  if ($[27] !== onThumbClick || $[28] !== slides) {
    t17 = index_2 => {
      const slide = slides[index_2];
      if (slide?.type === "video") {
        onThumbClick(index_2);
        openVideoModal();
        return;
      }
      onThumbClick(index_2);
    };
    $[27] = onThumbClick;
    $[28] = slides;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const handleThumbClick = t17;
  let t18;
  if ($[30] !== emblaAPI || $[31] !== emblaThumbsAPI) {
    t18 = () => {
      if (!emblaAPI) {
        return;
      }
      const index_3 = emblaAPI.selectedScrollSnap();
      setSelectedIndex(index_3);
      if (emblaThumbsAPI && window.matchMedia("(pointer: coarse)").matches) {
        emblaThumbsAPI.scrollTo(index_3);
      }
    };
    $[30] = emblaAPI;
    $[31] = emblaThumbsAPI;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const onSelect = t18;
  let t19;
  if ($[33] !== emblaAPI) {
    t19 = () => {
      if (!emblaAPI) {
        return;
      }
    };
    $[33] = emblaAPI;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const reInitialize = t19;
  let t20;
  if ($[35] !== emblaAPI) {
    t20 = () => {
      if (!emblaAPI) {
        return;
      }
      emblaAPI.scrollPrev();
    };
    $[35] = emblaAPI;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const scrollPrev = t20;
  let t21;
  if ($[37] !== emblaAPI) {
    t21 = () => {
      if (!emblaAPI) {
        return;
      }
      emblaAPI.scrollNext();
    };
    $[37] = emblaAPI;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const scrollNext = t21;
  let t22;
  if ($[39] !== scrollNext || $[40] !== scrollPrev) {
    t22 = event => {
      if (event.key == "ArrowLeft") {
        scrollPrev();
      }
      if (event.key == "ArrowRight") {
        scrollNext();
      }
    };
    $[39] = scrollNext;
    $[40] = scrollPrev;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const handleKeyDown = t22;
  let t23;
  if ($[42] !== emblaThumbsAPI) {
    t23 = () => {
      if (!emblaThumbsAPI) {
        return;
      }
      const canScroll = emblaThumbsAPI.canScrollPrev() || emblaThumbsAPI.canScrollNext();
      setArrowsHidden(!canScroll);
    };
    $[42] = emblaThumbsAPI;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  const updateArrowVisibility = t23;
  let t24;
  let t25;
  if ($[44] !== emblaAPI || $[45] !== emblaThumbsAPI || $[46] !== onSelect || $[47] !== reInitialize || $[48] !== updateArrowVisibility) {
    t24 = () => {
      if (!emblaAPI || !emblaThumbsAPI) {
        return;
      }
      onSelect();
      emblaAPI.on("select", onSelect);
      emblaAPI.on("reInit", reInitialize);
      updateArrowVisibility();
      const handleResize = () => {
        emblaThumbsAPI.reInit();
        updateArrowVisibility();
      };
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    };
    t25 = [emblaAPI, emblaThumbsAPI, onSelect, reInitialize, updateArrowVisibility];
    $[44] = emblaAPI;
    $[45] = emblaThumbsAPI;
    $[46] = onSelect;
    $[47] = reInitialize;
    $[48] = updateArrowVisibility;
    $[49] = t24;
    $[50] = t25;
  } else {
    t24 = $[49];
    t25 = $[50];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[51] !== carouselModalOpen || $[52] !== emblaAPI || $[53] !== handleKeyDown) {
    t26 = () => {
      if (!emblaAPI || carouselModalOpen) {
        return;
      }
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    };
    t27 = [emblaAPI, carouselModalOpen, handleKeyDown];
    $[51] = carouselModalOpen;
    $[52] = emblaAPI;
    $[53] = handleKeyDown;
    $[54] = t26;
    $[55] = t27;
  } else {
    t26 = $[54];
    t27 = $[55];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[56] !== emblaAPI || $[57] !== emblaThumbsAPI || $[58] !== productImages || $[59] !== variant) {
    t28 = () => {
      if (!emblaAPI) {
        return;
      }
      emblaAPI.reInit();
      if (emblaThumbsAPI) {
        emblaThumbsAPI.reInit();
      }
      if (variant && variant.variant_id) {
        setTimeout(() => {
          productImages.forEach((slide_0, index_4) => {
            if (variant.variant_id == slide_0.variant_id) {
              emblaAPI.scrollTo(index_4);
              if (emblaThumbsAPI) {
                emblaThumbsAPI.scrollTo(index_4);
              }
            }
          });
        }, 50);
      }
    };
    t29 = [variant, emblaAPI, emblaThumbsAPI, productImages];
    $[56] = emblaAPI;
    $[57] = emblaThumbsAPI;
    $[58] = productImages;
    $[59] = variant;
    $[60] = t28;
    $[61] = t29;
  } else {
    t28 = $[60];
    t29 = $[61];
  }
  useEffect(t28, t29);
  let t30;
  if ($[62] !== emblaAPI) {
    t30 = () => {
      const autoplay = emblaAPI?.plugins()?.autoplay;
      if (!emblaAPI || !autoplay) {
        return;
      }
      if (autoplay.isPlaying()) {
        autoplay.stop();
        setIsPlaying(false);
      } else {
        autoplay.play();
        setIsPlaying(true);
      }
    };
    $[62] = emblaAPI;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const play = t30;
  let t31;
  if ($[64] !== emblaThumbsAPI) {
    t31 = () => {
      if (!emblaThumbsAPI) {
        return;
      }
      const engine = emblaThumbsAPI.internalEngine();
      const slideWidth = engine.slideRects[0]?.width || 100;
      const slideCount = window.matchMedia("(max-width: 768px)").matches ? 2 : 3;
      engine.scrollTo.distance(slideWidth * slideCount, false);
    };
    $[64] = emblaThumbsAPI;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  const scrollLeft = t31;
  let t32;
  if ($[66] !== emblaThumbsAPI) {
    t32 = () => {
      if (!emblaThumbsAPI) {
        return;
      }
      const engine_0 = emblaThumbsAPI.internalEngine();
      const slideWidth_0 = engine_0.slideRects[0]?.width || 100;
      const slideCount_0 = window.matchMedia("(max-width: 768px)").matches ? 2 : 3;
      engine_0.scrollTo.distance(-slideWidth_0 * slideCount_0, false);
    };
    $[66] = emblaThumbsAPI;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  const scrollRight = t32;
  let t33;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = e => {
      if (!imageContainerRef.current) {
        return;
      }
      if (!window.matchMedia("(min-width: 1024px)").matches) {
        return;
      }
      setCursorPos({
        x: e.clientX,
        y: e.clientY
      });
    };
    $[68] = t33;
  } else {
    t33 = $[68];
  }
  const handleImageMouseMove = t33;
  let t34;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) {
        setCursorVisible(true);
      }
    };
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  const handleImageMouseEnter = t34;
  let t35;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => {
      setCursorVisible(false);
    };
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  const handleImageMouseLeave = t35;
  const length = slides.length;
  const progressNode = useRef(null);
  const {
    showAutoplayProgress
  } = useAutoplayProgress(emblaAPI, progressNode);
  let t36;
  if ($[71] !== sub_product?.sub_product_name) {
    t36 = decodeEntities(sub_product?.sub_product_name) || "Product";
    $[71] = sub_product?.sub_product_name;
    $[72] = t36;
  } else {
    t36 = $[72];
  }
  const name = t36;
  const mfr = sub_product?.supplier?.supplier_name;
  const fmt = _temp;
  const lo = parseFloat(sub_product?.lowest_price) || parseFloat(sub_product?.retail_price) || 0;
  const hi = parseFloat(sub_product?.highest_price) || 0;
  let price = "";
  if (lo > 0 && hi > lo) {
    price = `${fmt(lo)} to ${fmt(hi)}`;
  } else {
    if (lo > 0) {
      price = fmt(lo);
    }
  }
  let alt = mfr ? `${name} by ${mfr}` : name;
  if (price) {
    alt = alt + ` – ${price}`;
  }
  const heroAlt = alt;
  let t37;
  if ($[73] !== open) {
    t37 = <SiteCover close={closeModal} coverVisible={open} />;
    $[73] = open;
    $[74] = t37;
  } else {
    t37 = $[74];
  }
  let t38;
  if ($[75] !== carouselModalOpen || $[76] !== selectedIndex || $[77] !== slides || $[78] !== sub_product) {
    t38 = <ProductCarouselModal carouselModalOpen={carouselModalOpen} setCarouselModalOpen={setCarouselModalOpen} sub_product={sub_product} slides={slides} initialSlideIndex={selectedIndex} onPlayVideo={openVideoModal} />;
    $[75] = carouselModalOpen;
    $[76] = selectedIndex;
    $[77] = slides;
    $[78] = sub_product;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  const t39 = sub_product?.sub_product_name;
  let t40;
  if ($[80] !== t39) {
    t40 = decodeEntities(t39);
    $[80] = t39;
    $[81] = t40;
  } else {
    t40 = $[81];
  }
  let t41;
  if ($[82] !== t40 || $[83] !== video || $[84] !== videoModalOpen) {
    t41 = <VideoModal video={video} productName={t40} isOpen={videoModalOpen} setIsOpen={setVideoModalOpen} />;
    $[82] = t40;
    $[83] = video;
    $[84] = videoModalOpen;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  const t42 = `transition-all duration-100 bg-white ${open ? "fixed z-50 m-8 bg-white top-0 left-0 right-0 bottom-0 border" : ""}`;
  const firstImage = productImages[0];
  const aspectRatio = firstImage?.width && firstImage?.height ? `${firstImage.width} / ${firstImage.height}` : "4 / 3";
  let t43;
  if ($[86] !== aspectRatio || $[87] !== heroAlt || $[88] !== slides || $[89] !== sub_product?.sub_product_name) {
    let t44;
    if ($[91] !== aspectRatio || $[92] !== heroAlt || $[93] !== sub_product?.sub_product_name) {
      t44 = (slide_1, index_5) => {
        if (slide_1.type === "video") {
          return <div key={`video-${slide_1.videoId}`} className="embla__slide items-center justify-items-center relative group max-h-[min(546px,55vh)]" style={{
            aspectRatio
          }}><VideoSlide video={slide_1} productName={decodeEntities(sub_product?.sub_product_name)} onPlay={openVideoModal} /></div>;
        }
        const imagePath = slide_1.variant_id ? `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${slide_1.file_name}` : `${process.env.NEXT_PUBLIC_RESOURCE_URL}/full/${slide_1.file_name}`;
        return <div key={index_5} className="embla__slide items-center justify-items-center relative group max-h-[min(546px,55vh)]" style={{
          aspectRatio
        }}><Image className="w-full h-full object-contain" src={imagePath} overrideSrc={imagePath} alt={index_5 === 0 ? heroAlt : slide_1.alt_attribute || decodeEntities(sub_product?.sub_product_name) || "Product image"} fill={true} sizes="(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) calc(100vw - 48px), (max-width: 1279px) calc(100vw - 448px), (max-width: 1535px) calc(100vw - 488px), calc(100vw - 508px)" quality={90} loading={index_5 === 0 ? "eager" : "lazy"} fetchPriority={index_5 === 0 ? "high" : undefined} onClick={() => {
            if (window.matchMedia("(min-width: 1024px)").matches) {
              openModal();
            }
          }} /></div>;
      };
      $[91] = aspectRatio;
      $[92] = heroAlt;
      $[93] = sub_product?.sub_product_name;
      $[94] = t44;
    } else {
      t44 = $[94];
    }
    t43 = slides.map(t44);
    $[86] = aspectRatio;
    $[87] = heroAlt;
    $[88] = slides;
    $[89] = sub_product?.sub_product_name;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  const t44 = t43;
  let t45;
  if ($[95] !== t44) {
    t45 = <div className="embla__container items-center">{t44}</div>;
    $[95] = t44;
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== mainViewportRef || $[98] !== t45) {
    t46 = <div className="embla lg:cursor-none" ref={mainViewportRef}>{t45}</div>;
    $[97] = mainViewportRef;
    $[98] = t45;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== cursorPos || $[101] !== cursorVisible) {
    t47 = cursorVisible && <div className="pointer-events-none fixed z-[1100] hidden lg:flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md transition-opacity duration-150" style={{
      left: cursorPos.x,
      top: cursorPos.y,
      transform: "translate(-50%, -50%)"
    }}><FontAwesomeIcon icon={faExpand} className="text-dark text-sm" /></div>;
    $[100] = cursorPos;
    $[101] = cursorVisible;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== t46 || $[104] !== t47) {
    t48 = <div ref={imageContainerRef} className="relative mb-1" onMouseMove={handleImageMouseMove} onMouseEnter={handleImageMouseEnter} onMouseLeave={handleImageMouseLeave}>{t46}{t47}</div>;
    $[103] = t46;
    $[104] = t47;
    $[105] = t48;
  } else {
    t48 = $[105];
  }
  const t49 = `flex items-center mb-2 ${arrowsHidden ? "justify-center" : ""}`;
  let t50;
  if ($[106] !== arrowsHidden || $[107] !== scrollLeft) {
    t50 = !arrowsHidden && <button aria-label="Previous" onClick={scrollLeft} className="group min-w-[44px] min-h-[44px] flex items-center justify-center"><FontAwesomeIcon icon={faChevronLeft} className="text-xl text-light-3 font-extralight group-hover:text-primary mr-2" /></button>;
    $[106] = arrowsHidden;
    $[107] = scrollLeft;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  const t51 = `embla-thumbs min-w-0 ${arrowsHidden ? "mx-auto" : "flex-1"}`;
  let t52;
  if ($[109] !== handleThumbClick || $[110] !== onThumbHover || $[111] !== selectedIndex || $[112] !== slides || $[113] !== sub_product?.sub_product_name) {
    let t53;
    if ($[115] !== handleThumbClick || $[116] !== onThumbHover || $[117] !== selectedIndex || $[118] !== sub_product?.sub_product_name) {
      t53 = (slide_2, index_6) => <div key={slide_2.type === "video" ? `video-${slide_2.videoId}` : index_6} className="embla-thumbs__slide"><Thumb onClick={() => handleThumbClick(index_6)} onMouseEnter={() => onThumbHover(index_6)} selected={index_6 === selectedIndex} slide={slide_2} productName={decodeEntities(sub_product?.sub_product_name)} thumbNumber={index_6 + 1} /></div>;
      $[115] = handleThumbClick;
      $[116] = onThumbHover;
      $[117] = selectedIndex;
      $[118] = sub_product?.sub_product_name;
      $[119] = t53;
    } else {
      t53 = $[119];
    }
    t52 = slides.map(t53);
    $[109] = handleThumbClick;
    $[110] = onThumbHover;
    $[111] = selectedIndex;
    $[112] = slides;
    $[113] = sub_product?.sub_product_name;
    $[114] = t52;
  } else {
    t52 = $[114];
  }
  let t53;
  if ($[120] !== t52) {
    t53 = <div className="embla-thumbs__container h-16">{t52}</div>;
    $[120] = t52;
    $[121] = t53;
  } else {
    t53 = $[121];
  }
  let t54;
  if ($[122] !== emblaThumbsRef || $[123] !== t53) {
    t54 = <div className="embla-thumbs__viewport" ref={emblaThumbsRef}>{t53}</div>;
    $[122] = emblaThumbsRef;
    $[123] = t53;
    $[124] = t54;
  } else {
    t54 = $[124];
  }
  let t55;
  if ($[125] !== t51 || $[126] !== t54) {
    t55 = <div className={t51}>{t54}</div>;
    $[125] = t51;
    $[126] = t54;
    $[127] = t55;
  } else {
    t55 = $[127];
  }
  let t56;
  if ($[128] !== arrowsHidden || $[129] !== scrollRight) {
    t56 = !arrowsHidden && <button aria-label="Next" onClick={scrollRight} className="group min-w-[44px] min-h-[44px] flex items-center justify-center"><FontAwesomeIcon icon={faChevronRight} className="text-xl text-light-3 font-extralight group-hover:text-primary ml-2" /></button>;
    $[128] = arrowsHidden;
    $[129] = scrollRight;
    $[130] = t56;
  } else {
    t56 = $[130];
  }
  let t57;
  if ($[131] !== t49 || $[132] !== t50 || $[133] !== t55 || $[134] !== t56) {
    t57 = <div className={t49}>{t50}{t55}{t56}</div>;
    $[131] = t49;
    $[132] = t50;
    $[133] = t55;
    $[134] = t56;
    $[135] = t57;
  } else {
    t57 = $[135];
  }
  let t58;
  if ($[136] !== length || $[137] !== selectedIndex) {
    t58 = length > 0 && <div className="text-xs md:text-xs text-light-3 font-extralight"><span className="text-dark">{selectedIndex + 1}</span> / <span className="text-black">{length}</span></div>;
    $[136] = length;
    $[137] = selectedIndex;
    $[138] = t58;
  } else {
    t58 = $[138];
  }
  let t59;
  if ($[139] !== t58) {
    t59 = <div className="hidden flex items-center justify-end">{t58}</div>;
    $[139] = t58;
    $[140] = t59;
  } else {
    t59 = $[140];
  }
  let t60;
  if ($[141] !== play) {
    t60 = () => play();
    $[141] = play;
    $[142] = t60;
  } else {
    t60 = $[142];
  }
  const t61 = isPlaying ? "Pause carousel" : "Play carousel";
  let t62;
  if ($[143] !== isPlaying) {
    t62 = isPlaying && <FontAwesomeIcon icon={faPause} className="text-xs text-[#c8c8c8] group-hover:text-primary" />;
    $[143] = isPlaying;
    $[144] = t62;
  } else {
    t62 = $[144];
  }
  let t63;
  if ($[145] !== isPlaying) {
    t63 = !isPlaying && <FontAwesomeIcon icon={faPlay} className="text-xs text-[#c8c8c8] group-hover:text-primary" />;
    $[145] = isPlaying;
    $[146] = t63;
  } else {
    t63 = $[146];
  }
  let t64;
  if ($[147] !== t60 || $[148] !== t61 || $[149] !== t62 || $[150] !== t63) {
    t64 = <div className="hidden flex items-center justify-end"><button onClick={t60} className="group p-0 m-0 text-[0] pb-[.05rem]" aria-label={t61}>{t62}{t63}</button></div>;
    $[147] = t60;
    $[148] = t61;
    $[149] = t62;
    $[150] = t63;
    $[151] = t64;
  } else {
    t64 = $[151];
  }
  let t65;
  if ($[152] !== showAutoplayProgress) {
    t65 = "flex items-center justify-end mt-2 lg:mr-4 embla__progress".concat(showAutoplayProgress ? "" : " embla__progress--hidden");
    $[152] = showAutoplayProgress;
    $[153] = t65;
  } else {
    t65 = $[153];
  }
  let t66;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <div className="embla__progress__bar" ref={progressNode} />;
    $[154] = t66;
  } else {
    t66 = $[154];
  }
  let t67;
  if ($[155] !== t65) {
    t67 = <div className={t65}>{t66}</div>;
    $[155] = t65;
    $[156] = t67;
  } else {
    t67 = $[156];
  }
  let t68;
  if ($[157] !== t42 || $[158] !== t48 || $[159] !== t57 || $[160] !== t59 || $[161] !== t64 || $[162] !== t67) {
    t68 = <div className={t42}>{t48}{t57}{t59}{t64}{t67}</div>;
    $[157] = t42;
    $[158] = t48;
    $[159] = t57;
    $[160] = t59;
    $[161] = t64;
    $[162] = t67;
    $[163] = t68;
  } else {
    t68 = $[163];
  }
  let t69;
  if ($[164] !== t37 || $[165] !== t38 || $[166] !== t41 || $[167] !== t68) {
    t69 = <div className="product-carousel">{t37}{t38}{t41}{t68}</div>;
    $[164] = t37;
    $[165] = t38;
    $[166] = t41;
    $[167] = t68;
    $[168] = t69;
  } else {
    t69 = $[168];
  }
  return t69;
};
export default ProductCarousel;
function _temp(n) {
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
}